/**
 * @file TiptapEditor.tsx
 * @description Wysiwyg-редактор статей блога (phase12.0).
 *
 * Расширения:
 *   - StarterKit (paragraph, h2/h3, lists, bold/italic, blockquote, code-block, hr).
 *   - Image — drag-drop + upload в MinIO через `/admin/content/media`.
 *   - Link — paste-URL автоматически становится ссылкой.
 *   - YouTube — вставка embed по ссылке.
 *   - Placeholder — подсказка в пустом редакторе.
 *   - CalloutNode (кастомная) — цветной блок-«важно»/«совет»/«предупреждение».
 *   - QuoteWithAuthorNode (кастомная) — цитата с подписью автора.
 *
 * Контракт: получает initial value (Tiptap JSON) и колбэк onChange(json, html).
 * Tailwind Typography (`prose`) даёт нативные стили H2/H3/P/UL/etc.
 */

import { Image as ImageExtension } from "@tiptap/extension-image";
import { Placeholder as PlaceholderExtension } from "@tiptap/extension-placeholder";
import { Youtube as YoutubeExtension } from "@tiptap/extension-youtube";
import { EditorContent, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import {
  Bold,
  Code,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Info,
  Italic,
  Lightbulb,
  Link as LinkIcon,
  List,
  ListOrdered,
  Loader2,
  Minus,
  Quote,
  Redo,
  Undo,
  Video as YoutubeIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { uploadContentMedia } from "../../lib/api-content-media";
import { useNotify } from "../ui/NotifyProvider";

import { CalloutNode, type CalloutVariant } from "./tiptap-extensions/CalloutNode";
import { QuoteWithAuthorNode } from "./tiptap-extensions/QuoteWithAuthorNode";

interface TiptapEditorProps {
  value: unknown;
  onChange: (json: Record<string, unknown>, html: string) => void;
  placeholder?: string;
  className?: string;
}

export function TiptapEditor({ value, onChange, placeholder, className }: TiptapEditorProps) {
  const notify = useNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      // Ссылка входит в StarterKit v3 — настраиваем там, без второго подключения.
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true } }),
      ImageExtension.configure({ inline: false, allowBase64: false }),
      YoutubeExtension.configure({
        controls: true,
        nocookie: true,
        HTMLAttributes: { class: "aspect-video w-full" },
      }),
      PlaceholderExtension.configure({
        placeholder: placeholder ?? "Начните писать…",
      }),
      CalloutNode,
      QuoteWithAuthorNode,
    ],
    content: (value as Record<string, unknown>) ?? { type: "doc", content: [] },
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[400px] focus:outline-none px-5 py-4 prose-headings:font-bold prose-h2:text-2xl prose-h3:text-xl prose-blockquote:border-l-4 prose-blockquote:border-primary/40 prose-blockquote:pl-4 prose-blockquote:italic prose-a:text-primary prose-img:rounded-md",
      },
    },
    onUpdate({ editor }) {
      onChange(
        editor.getJSON() as unknown as Record<string, unknown>,
        editor.getHTML(),
      );
    },
  });

  // emitUpdate: false — в TipTap v3 setContent по умолчанию эмитит update;
  // без флага programmatic-синхронизация помечала статью как изменённую.
  useEffect(() => {
    if (!editor || !value) return;
    const current = JSON.stringify(editor.getJSON());
    const next = JSON.stringify(value);
    if (current !== next) {
      editor.commands.setContent(value as Record<string, unknown>, {
        emitUpdate: false,
      });
    }
  }, [editor, value]);

  if (!editor) return null;

  async function onPickImage(file: File) {
    setUploading(true);
    try {
      const asset = await uploadContentMedia(file, "");
      editor?.chain().focus().setImage({ src: asset.url, alt: file.name }).run();
      notify.success("Картинка загружена");
    } catch (err) {
      notify.error(err instanceof Error ? err.message : "Ошибка загрузки");
    } finally {
      setUploading(false);
    }
  }

  function promptLink() {
    const prev = editor!.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL ссылки (оставьте пусто для удаления):", prev ?? "");
    if (url === null) return;
    if (url === "") {
      editor!.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor!.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  }

  function promptYoutube() {
    const url = window.prompt("Ссылка YouTube:", "https://www.youtube.com/watch?v=");
    if (!url) return;
    editor!.commands.setYoutubeVideo({ src: url });
  }

  return (
    <div
      className={`flex flex-col rounded-md border border-on-surface/20 bg-surface ${className ?? ""}`}
    >
      <div className="flex flex-wrap items-center gap-1 border-b border-on-surface/20 bg-surface-2 p-2">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          aria="Заголовок 2"
        >
          <Heading2 className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}
          aria="Заголовок 3"
        >
          <Heading3 className="h-4 w-4" />
        </ToolbarButton>
        <Sep />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          aria="Жирный"
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          aria="Курсив"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton onClick={promptLink} active={editor.isActive("link")} aria="Ссылка">
          <LinkIcon className="h-4 w-4" />
        </ToolbarButton>
        <Sep />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          aria="Маркированный список"
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          aria="Нумерованный список"
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
          aria="Цитата"
        >
          <Quote className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          active={editor.isActive("codeBlock")}
          aria="Блок кода"
        >
          <Code className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          aria="Горизонтальная линия"
        >
          <Minus className="h-4 w-4" />
        </ToolbarButton>
        <Sep />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPickImage(f);
            e.target.value = "";
          }}
        />
        <ToolbarButton
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          aria={uploading ? "Загрузка…" : "Вставить изображение"}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ImageIcon className="h-4 w-4" />
          )}
        </ToolbarButton>
        <ToolbarButton onClick={promptYoutube} aria="Вставить YouTube">
          <YoutubeIcon className="h-4 w-4" />
        </ToolbarButton>
        <Sep />
        <CalloutMenu
          onInsert={(variant) => editor.chain().focus().insertCallout(variant).run()}
        />
        <ToolbarButton
          onClick={() => editor.chain().focus().insertQuoteWithAuthor().run()}
          aria="Цитата с автором"
        >
          <span className="text-[10px] font-semibold">«А»</span>
        </ToolbarButton>
        <Sep />
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          aria="Отменить"
        >
          <Undo className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          aria="Повторить"
        >
          <Redo className="h-4 w-4" />
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} className="flex-1 overflow-auto" />
    </div>
  );
}

function CalloutMenu({ onInsert }: { onInsert: (v: CalloutVariant) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <ToolbarButton onClick={() => setOpen((o) => !o)} active={open} aria="Callout">
        <Info className="h-4 w-4" />
      </ToolbarButton>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-20 mt-1 flex flex-col rounded-md border border-on-surface/20 bg-surface shadow-lg">
            {[
              { v: "info" as const, label: "Info", icon: Info },
              { v: "tip" as const, label: "Совет", icon: Lightbulb },
              { v: "warning" as const, label: "Внимание", icon: Info },
            ].map(({ v, label, icon: Icon }) => (
              <button
                key={v}
                type="button"
                onClick={() => {
                  onInsert(v);
                  setOpen(false);
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-on-surface/5"
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ToolbarButton({
  onClick,
  active,
  disabled,
  aria,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  aria: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={aria}
      title={aria}
      className={`inline-flex h-8 w-8 items-center justify-center rounded transition-colors ${
        active
          ? "bg-on-surface/15 text-on-surface"
          : "text-on-surface-muted hover:bg-on-surface/10"
      } disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function Sep() {
  return <span className="mx-1 h-6 w-px bg-on-surface/20" />;
}
